import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Search, MoreVertical, Eye, Pencil, MessageSquare, CalendarPlus, Phone, Mail, Trash2, ChevronLeft, ChevronRight, Download, Upload, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, UserCheck, Users } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const STAGE_COLORS = {
  'Cold Lead': 'bg-blue-100 text-blue-800', 'Enquiry': 'bg-indigo-100 text-indigo-800',
  'Intro Phone Call': 'bg-yellow-100 text-yellow-800', "Child's Call Consultancy": 'bg-yellow-100 text-yellow-800',
  "Parent's Call Consultancy": 'bg-orange-100 text-orange-800', 'College F2F': 'bg-pink-100 text-pink-800',
  'Counselling': 'bg-purple-100 text-purple-800', 'Open Day': 'bg-cyan-100 text-cyan-800',
  'Documentation': 'bg-amber-100 text-amber-800', 'Registration': 'bg-emerald-100 text-emerald-800',
  'Fee Paid': 'bg-green-100 text-green-800', 'Refunded': 'bg-red-100 text-red-800',
  'Lost': 'bg-red-100 text-red-800',
};

export default function AllLeadsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [consultantSearch, setConsultantSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [masters, setMasters] = useState({ courses: [], stages: [], sources: [], triggers: [], users: [], cities: [], states: [], schools: [] });
  const [noteDialog, setNoteDialog] = useState({ open: false, leadId: null });
  const [noteContent, setNoteContent] = useState('');
  const [followUpDialog, setFollowUpDialog] = useState({ open: false, leadId: null });
  const [followUpData, setFollowUpData] = useState({ follow_up_date: '', next_action: '' });
  const [stageDialog, setStageDialog] = useState({ open: false, leadId: null });
  const [newStageId, setNewStageId] = useState('');
  const [importDialog, setImportDialog] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkCounsellorDialog, setBulkCounsellorDialog] = useState(false);
  const [bulkConsultantDialog, setBulkConsultantDialog] = useState(false);
  const [bulkCounsellor, setBulkCounsellor] = useState('');
  const [bulkConsultant, setBulkConsultant] = useState('');
  const [consultants, setConsultants] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  useEffect(() => {
    Promise.all([
      API.get('/masters/courses'), API.get('/masters/lead-stages'),
      API.get('/masters/lead-sources'), API.get('/masters/lead-triggers'), API.get('/users'),
      API.get('/leads/filter-options'), API.get('/consultants')
    ]).then(([c, st, so, tr, u, fo, con]) => {
      setMasters({
        courses: c.data, stages: st.data, sources: so.data, triggers: tr.data, users: u.data,
        cities: fo.data.cities || [], states: fo.data.states || [], schools: fo.data.schools || []
      });
      setConsultants(con.data);
    }).catch(() => {});
  }, []);

  const fetchLeads = useCallback(() => {
    const params = { page, limit: 50 };
    if (search) params.search = search;
    if (consultantSearch) params.consultant_search = consultantSearch;
    if (sortBy) { params.sort_by = sortBy; params.sort_order = sortOrder; }
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    API.get('/leads', { params }).then(r => {
      setLeads(r.data.leads);
      setTotal(r.data.total);
      setPages(r.data.pages);
    }).catch(() => toast.error('Failed to load leads'));
  }, [page, search, consultantSearch, filters, sortBy, sortOrder]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    try {
      await API.post(`/leads/${noteDialog.leadId}/notes`, { content: noteContent, note_type: 'general' });
      toast.success('Note added');
      setNoteDialog({ open: false, leadId: null });
      setNoteContent('');
    } catch { toast.error('Failed to add note'); }
  };

  const handleAddFollowUp = async () => {
    if (!followUpData.follow_up_date) return;
    try {
      await API.post(`/leads/${followUpDialog.leadId}/follow-ups`, followUpData);
      toast.success('Follow up scheduled');
      setFollowUpDialog({ open: false, leadId: null });
      setFollowUpData({ follow_up_date: '', next_action: '' });
      fetchLeads();
    } catch { toast.error('Failed to schedule follow up'); }
  };

  const handleChangeStage = async () => {
    if (!newStageId) return;
    try {
      await API.put(`/leads/${stageDialog.leadId}/stage`, { stage_id: newStageId });
      toast.success('Stage updated');
      setStageDialog({ open: false, leadId: null });
      fetchLeads();
    } catch { toast.error('Failed to update stage'); }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await API.delete(`/leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
    } catch { toast.error('Failed to delete lead'); }
  };

  const toggleSelect = (id) => {
    setSelectedLeads(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    setSelectedLeads(prev => prev.length === leads.length ? [] : leads.map(l => l.id));
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Permanently delete ${selectedLeads.length} lead(s)?`)) return;
    try {
      await API.post('/leads/bulk-action', { action: 'delete', lead_ids: selectedLeads });
      toast.success(`${selectedLeads.length} leads deleted`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const bulkAssignCounsellor = async () => {
    if (!bulkCounsellor) { toast.error('Select a counsellor'); return; }
    try {
      await API.post('/leads/bulk-action', { action: 'assign_counsellor', lead_ids: selectedLeads, counsellor_id: bulkCounsellor });
      toast.success(`${selectedLeads.length} leads assigned`);
      setSelectedLeads([]);
      setBulkCounsellorDialog(false);
      setBulkCounsellor('');
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const bulkAssignConsultant = async () => {
    if (!bulkConsultant) { toast.error('Select a consultant'); return; }
    try {
      await API.post('/leads/bulk-action', { action: 'assign_consultant', lead_ids: selectedLeads, consultant_id: bulkConsultant });
      toast.success(`${selectedLeads.length} leads assigned`);
      setSelectedLeads([]);
      setBulkConsultantDialog(false);
      setBulkConsultant('');
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleExport = async () => {
    try {
      const res = await API.get('/leads/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads_export.xlsx';
      a.click();
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const CHUNK_SIZE = 512 * 1024; // 512KB chunks to stay under ingress limit

    if (file.size > CHUNK_SIZE) {
      // Chunked upload for large files
      setImportProgress({ status: 'uploading', message: 'Uploading file in chunks...', total_rows: 0 });
      try {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const initRes = await API.post('/leads/import-chunked/init', {
          file_name: file.name, total_chunks: totalChunks, file_size: file.size
        });
        const uploadId = initRes.data.upload_id;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          const reader = new FileReader();
          const base64 = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(chunk);
          });
          await API.post('/leads/import-chunked/upload', {
            upload_id: uploadId, chunk_index: i, data: base64
          });
          setImportProgress(prev => ({ ...prev, message: `Uploading chunk ${i + 1}/${totalChunks}...` }));
        }

        setImportProgress(prev => ({ ...prev, status: 'processing', message: 'Processing rows...' }));
        const res = await API.post('/leads/import-chunked/process', { upload_id: uploadId }, { timeout: 600000 });

        if (res.data.import_id) {
          setImportProgress({ status: 'processing', total_rows: res.data.total_rows, processed: 0, importId: res.data.import_id });
          pollImportProgress(res.data.import_id);
        } else {
          toast.success(`Import complete: ${res.data.inserted} inserted, ${res.data.duplicates} duplicates, ${res.data.skipped} skipped`);
          setImportDialog(false);
          setImportProgress(null);
          fetchLeads();
        }
      } catch {
        toast.error('Import failed');
        setImportProgress(null);
      }
    } else {
      // Direct upload for small files
      const formData = new FormData();
      formData.append('file', file);
      setImportProgress({ status: 'uploading', message: 'Uploading file...' });
      try {
        const res = await API.post('/leads/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000
        });
        if (res.data.import_id) {
          setImportProgress({ status: 'processing', total_rows: res.data.total_rows, processed: 0, importId: res.data.import_id });
          pollImportProgress(res.data.import_id);
        } else {
          toast.success(`Import complete: ${res.data.inserted} inserted, ${res.data.duplicates} duplicates, ${res.data.skipped} skipped`);
          setImportDialog(false);
          setImportProgress(null);
          fetchLeads();
        }
      } catch {
        toast.error('Import failed');
        setImportProgress(null);
      }
    }
  };

  const pollImportProgress = (importId) => {
    const interval = setInterval(async () => {
      try {
        const res = await API.get(`/leads/import-progress/${importId}`);
        const job = res.data;
        setImportProgress(prev => ({ ...prev, ...job }));
        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(interval);
          if (job.status === 'completed') {
            toast.success(`Import complete: ${job.inserted} inserted, ${job.duplicates} duplicates, ${job.skipped} skipped, ${job.failed} failed`);
            fetchLeads();
          } else {
            toast.error(`Import failed: ${job.error || 'Unknown error'}`);
          }
          setTimeout(() => { setImportProgress(null); setImportDialog(false); }, 2000);
        }
      } catch {
        clearInterval(interval);
        setImportProgress(null);
      }
    }, 2000);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      if (sortOrder === 'asc') setSortOrder('desc');
      else { setSortBy(''); setSortOrder(''); }
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} className="ml-1 opacity-30" />;
    return sortOrder === 'asc' ? <ArrowUp size={12} className="ml-1" /> : <ArrowDown size={12} className="ml-1" />;
  };

  return (
    <div className="p-6 space-y-4" data-testid="all-leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>All Leads</h1>
          <p className="text-sm text-slate-500 mt-1">{total} lead{total !== 1 ? 's' : ''} found</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-52">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9 text-sm" placeholder="Search by name, phone, email..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="search-input" />
          </div>
          <div className="relative w-52">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9 text-sm" placeholder="Search by consultant..." value={consultantSearch}
              onChange={e => { setConsultantSearch(e.target.value); setPage(1); }} data-testid="consultant-search-input" />
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn">
            <Download size={14} className="mr-1" /> Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportDialog(true)} data-testid="import-btn">
            <Upload size={14} className="mr-1" /> Import
          </Button>
          <Button size="sm" onClick={() => navigate('/leads/new')} data-testid="new-lead-btn" className="bg-slate-900 hover:bg-slate-800">
            + New Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-lg border border-slate-200">
        <Select value={filters.course || "all"} onValueChange={v => { setFilters(p => ({...p, course: v === 'all' ? undefined : v})); setPage(1); }}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Course" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Courses</SelectItem>, ...masters.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)]}</SelectContent>
        </Select>
        <Select value={filters.stage || "all"} onValueChange={v => { setFilters(p => ({...p, stage: v === 'all' ? undefined : v})); setPage(1); }}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Stages</SelectItem>, ...masters.stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)]}</SelectContent>
        </Select>
        <Select value={filters.source || "all"} onValueChange={v => { setFilters(p => ({...p, source: v === 'all' ? undefined : v})); setPage(1); }}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Sources</SelectItem>, ...masters.sources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)]}</SelectContent>
        </Select>
        <Select value={filters.counsellor || "all"} onValueChange={v => { setFilters(p => ({...p, counsellor: v === 'all' ? undefined : v})); setPage(1); }}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="filter-counsellor"><SelectValue placeholder="Counsellor" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Counsellors</SelectItem>, ...masters.users.filter(u => u.role === 'counsellor').map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)]}</SelectContent>
        </Select>
        <Select value={filters.city || "all"} onValueChange={v => { setFilters(p => ({...p, city: v === 'all' ? undefined : v})); setPage(1); }}>
          <SelectTrigger className="w-32 h-9 text-xs" data-testid="filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Cities</SelectItem>, ...masters.cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)]}</SelectContent>
        </Select>
        <Select value={filters.state || "all"} onValueChange={v => { setFilters(p => ({...p, state: v === 'all' ? undefined : v})); setPage(1); }}>
          <SelectTrigger className="w-32 h-9 text-xs" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All States</SelectItem>, ...masters.states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)]}</SelectContent>
        </Select>
        <Select value={filters.school || "all"} onValueChange={v => { setFilters(p => ({...p, school: v === 'all' ? undefined : v})); setPage(1); }}>
          <SelectTrigger className="w-40 h-9 text-xs" data-testid="filter-school"><SelectValue placeholder="12th School" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Schools</SelectItem>, ...masters.schools.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)]}</SelectContent>
        </Select>
      </div>

      {/* Bulk Action Bar */}
      {selectedLeads.length > 0 && (
        <div className="flex items-center gap-3 bg-slate-900 text-white px-4 py-2.5 rounded-lg" data-testid="bulk-action-bar">
          <Checkbox checked={selectedLeads.length === leads.length} onCheckedChange={toggleSelectAll} className="border-white data-[state=checked]:bg-white data-[state=checked]:text-slate-900" />
          <span className="text-sm font-medium">{selectedLeads.length} selected</span>
          <div className="h-4 w-px bg-slate-600" />
          <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 text-xs gap-1.5" onClick={() => setBulkCounsellorDialog(true)} data-testid="bulk-assign-counsellor-btn">
            <UserCheck size={14} /> Assign Counsellor
          </Button>
          <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 text-xs gap-1.5" onClick={() => setBulkConsultantDialog(true)} data-testid="bulk-assign-consultant-btn">
            <Users size={14} /> Assign Consultant
          </Button>
          <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-900/30 text-xs gap-1.5" onClick={bulkDelete} data-testid="bulk-delete-btn">
            <Trash2 size={14} /> Delete
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white text-xs" onClick={() => setSelectedLeads([])}>Clear</Button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">
                  <Checkbox checked={leads.length > 0 && selectedLeads.length === leads.length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('lead_uid')} data-testid="sort-lead-id">
                  <span className="flex items-center">Lead ID<SortIcon field="lead_uid" /></span>
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('name')} data-testid="sort-name">
                  <span className="flex items-center">Name<SortIcon field="name" /></span>
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('phone')} data-testid="sort-phone">
                  <span className="flex items-center">Phone<SortIcon field="phone" /></span>
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Course</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Stage</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Counsellor</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort('created_at')} data-testid="sort-created">
                  <span className="flex items-center">Created<SortIcon field="created_at" /></span>
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-sm text-slate-400">No leads found</TableCell></TableRow>
              ) : leads.map(lead => (
                <TableRow key={lead.id} className={`hover:bg-slate-50 cursor-pointer ${selectedLeads.includes(lead.id) ? 'bg-slate-50' : ''}`} data-testid={`lead-row-${lead.id}`}>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <Checkbox checked={selectedLeads.includes(lead.id)} onCheckedChange={() => toggleSelect(lead.id)} data-testid={`select-lead-${lead.id}`} />
                  </TableCell>
                  <TableCell className="text-xs font-mono text-slate-500" onClick={() => navigate(`/leads/${lead.id}`)}>{lead.lead_uid}</TableCell>
                  <TableCell className="font-medium text-sm text-slate-900" onClick={() => navigate(`/leads/${lead.id}`)}>{lead.full_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{lead.phone_number}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{lead.course_name || '-'}</Badge></TableCell>
                  <TableCell>
                    <Badge className={`text-xs font-medium ${STAGE_COLORS[lead.stage_name] || 'bg-slate-100 text-slate-700'}`}>{lead.stage_name || '-'}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{(lead.source_name && lead.source_name.toLowerCase().includes('consultant') && lead.primary_consultant_name) ? lead.primary_consultant_name : (lead.source_name || '-')}</TableCell>
                  <TableCell className="text-xs text-slate-500">{lead.counsellor_name || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`lead-actions-${lead.id}`}>
                          <MoreVertical size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}><Eye size={14} className="mr-2" /> View</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStageDialog({ open: true, leadId: lead.id })}><ArrowUpDown size={14} className="mr-2" /> Change Stage</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setNoteDialog({ open: true, leadId: lead.id })}><MessageSquare size={14} className="mr-2" /> Add Note</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setFollowUpDialog({ open: true, leadId: lead.id })}><CalendarPlus size={14} className="mr-2" /> Schedule Follow Up</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`https://wa.me/${lead.phone_number?.replace(/\D/g,'')}`, '_blank')}><Phone size={14} className="mr-2" /> WhatsApp</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`mailto:${lead.email}`, '_blank')}><Mail size={14} className="mr-2" /> Email</DropdownMenuItem>
                        {user?.role === 'admin' && (
                          <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-red-600"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">Page {page} of {pages} ({total} leads)</p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 w-7 p-0">
                <ChevronLeft size={14} />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="h-7 w-7 p-0">
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={v => setNoteDialog({ open: v, leadId: noteDialog.leadId })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <Textarea placeholder="Enter note..." value={noteContent} onChange={e => setNoteContent(e.target.value)} data-testid="note-content-input" />
          <Button onClick={handleAddNote} className="bg-slate-900 hover:bg-slate-800" data-testid="save-note-btn">Save Note</Button>
        </DialogContent>
      </Dialog>

      {/* Follow Up Dialog */}
      <Dialog open={followUpDialog.open} onOpenChange={v => setFollowUpDialog({ open: v, leadId: followUpDialog.leadId })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule Follow Up</DialogTitle></DialogHeader>
          <Input type="date" value={followUpData.follow_up_date} onChange={e => setFollowUpData(p => ({...p, follow_up_date: e.target.value}))} data-testid="followup-date-input" />
          <Input placeholder="Next action..." value={followUpData.next_action} onChange={e => setFollowUpData(p => ({...p, next_action: e.target.value}))} />
          <Button onClick={handleAddFollowUp} className="bg-slate-900 hover:bg-slate-800" data-testid="save-followup-btn">Schedule</Button>
        </DialogContent>
      </Dialog>

      {/* Stage Dialog */}
      <Dialog open={stageDialog.open} onOpenChange={v => setStageDialog({ open: v, leadId: stageDialog.leadId })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Stage</DialogTitle></DialogHeader>
          <Select value={newStageId} onValueChange={setNewStageId}>
            <SelectTrigger data-testid="stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
            <SelectContent>{masters.stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={handleChangeStage} className="bg-slate-900 hover:bg-slate-800" data-testid="save-stage-btn">Update Stage</Button>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={v => { if (!importProgress) setImportDialog(v); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import Leads</DialogTitle></DialogHeader>
          {importProgress ? (
            <div className="space-y-3" data-testid="import-progress">
              <p className="text-sm font-medium text-slate-700">
                {importProgress.status === 'uploading' ? 'Uploading file...' :
                 importProgress.status === 'processing' ? 'Processing rows...' :
                 importProgress.status === 'completed' ? 'Import complete!' :
                 importProgress.status === 'failed' ? 'Import failed' : 'Working...'}
              </p>
              {importProgress.total_rows > 0 && (
                <>
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-slate-800 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.round(((importProgress.processed || importProgress.inserted || 0) / importProgress.total_rows) * 100))}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{(importProgress.processed || importProgress.inserted || 0).toLocaleString()} / {importProgress.total_rows.toLocaleString()} rows</span>
                    <span>{Math.min(100, Math.round(((importProgress.processed || importProgress.inserted || 0) / importProgress.total_rows) * 100))}%</span>
                  </div>
                  {importProgress.inserted > 0 && (
                    <p className="text-xs text-slate-500">
                      Inserted: {importProgress.inserted.toLocaleString()} | Duplicates: {(importProgress.duplicates || 0).toLocaleString()} | Skipped: {(importProgress.skipped || 0).toLocaleString()} | Failed: {(importProgress.failed || 0).toLocaleString()}
                    </p>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-2">Upload an Excel (.xlsx) or CSV file. Use the Export file as a template — it contains all supported columns. Supports up to 15 lakh rows.</p>
              <details className="text-xs text-slate-400">
                <summary className="cursor-pointer hover:text-slate-600">View all supported columns</summary>
                <p className="mt-1 leading-relaxed">
                  <strong>Personal:</strong> First Name, Last Name, Email, Phone, Alternate Phone, Gender, Date of Birth, Age, Aadhaar Number, Address Line 1, Address Line 2, City, State, Pin Code<br/>
                  <strong>Parents:</strong> Parent 1 Relation/Name/Phone/Email/Occupation/Annual Income, Parent 2 Relation/Name/Phone/Email/Occupation/Annual Income<br/>
                  <strong>Academic:</strong> Class 10 School/Board/Year/Percentage, Class 12 School/Board/Year/Percentage/Stream/Subjects<br/>
                  <strong>Exams:</strong> Exam 1 Type/Score/Rank/Year, Exam 2 Type/Score/Rank/Year<br/>
                  <strong>Course:</strong> Interested Course, Interested Sub-Course<br/>
                  <strong>Source:</strong> Primary Source, Secondary Source, Trigger, Counsellor, Primary Consultant, Secondary Consultant, Stage
                </p>
              </details>
              <Input type="file" accept=".xlsx,.csv" onChange={handleImport} data-testid="import-file-input" />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Counsellor Dialog */}
      <Dialog open={bulkCounsellorDialog} onOpenChange={setBulkCounsellorDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Counsellor to {selectedLeads.length} Lead(s)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Select Counsellor</Label>
            <Select value={bulkCounsellor || 'none'} onValueChange={v => setBulkCounsellor(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="bulk-counsellor-select"><SelectValue placeholder="Choose counsellor..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Select —</SelectItem>
                {masters.users.filter(u => ['admin', 'counsellor', 'admission_head'].includes(u.role)).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={bulkAssignCounsellor} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="bulk-counsellor-submit">Assign</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Consultant Dialog */}
      <Dialog open={bulkConsultantDialog} onOpenChange={setBulkConsultantDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Consultant to {selectedLeads.length} Lead(s)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-slate-500 uppercase">Select Consultant</Label>
            <Select value={bulkConsultant || 'none'} onValueChange={v => setBulkConsultant(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="bulk-consultant-select"><SelectValue placeholder="Choose consultant..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Select —</SelectItem>
                {consultants.filter(c => c.is_active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={bulkAssignConsultant} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="bulk-consultant-submit">Assign</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
